import { useState } from 'react';
import { TeamPlayer } from '../../types';
import { VideoGenerator } from '../../services/videoGenerator';
import { Button } from '../common/Button';

interface TeamRevealModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamA: TeamPlayer[];
  teamB: TeamPlayer[];
  bibsTeam: 'A' | 'B';
}

export function TeamRevealModal({
  isOpen,
  onClose,
  teamA,
  teamB,
  bibsTeam,
}: TeamRevealModalProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateVideo = async () => {
    setIsGenerating(true);
    setProgress(0);
    setStatus('Initializing...');
    setError(null);
    setVideoBlob(null);

    try {
      const generator = new VideoGenerator();
      const blob = await generator.generateVideo({
        teamA,
        teamB,
        bibsTeam,
        onProgress: (prog, stat) => {
          setProgress(prog);
          setStatus(stat);
        },
      });

      setVideoBlob(blob);
      setStatus('Video ready!');
    } catch (err) {
      console.error('Video generation failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate video');
      setStatus('');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!videoBlob) return;
    VideoGenerator.downloadVideo(videoBlob, `team-reveal-${new Date().toISOString().split('T')[0]}.webm`);
  };

  const handleShare = async () => {
    if (!videoBlob) return;

    try {
      const file = new File([videoBlob], 'team-reveal.webm', { type: 'video/webm' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        // Use Web Share API (mobile)
        await navigator.share({
          files: [file],
          title: 'Team Reveal',
          text: '⚽ Check out the team selections!',
        });
      } else {
        // Fallback: download the file
        alert('Sharing not supported on this device. The video will be downloaded instead.');
        handleDownload();
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
        alert('Failed to share video. Try downloading instead.');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              🎬 Team Reveal Video
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
              📹 About the Video
            </h3>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• FIFA-style player cards revealed one by one</li>
              <li>• Team A players → Team B players → Final summary</li>
              <li>• Duration: ~{Math.ceil((teamA.length + teamB.length) * 1.5 + 9)} seconds</li>
              <li>• Format: WebM video (works on WhatsApp)</li>
              <li>• Players without cards will show placeholder designs</li>
            </ul>
          </div>

          {/* Team Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Team A {bibsTeam === 'A' && <span className="text-yellow-500">(Bibs)</span>}
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                {teamA.map((p) => (
                  <li key={p.player_id}>
                    {p.name}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                Team B {bibsTeam === 'B' && <span className="text-yellow-500">(Bibs)</span>}
              </h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                {teamB.map((p) => (
                  <li key={p.player_id}>
                    {p.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Progress Section */}
          {isGenerating && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-300">{status}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-200">
                ❌ {error}
              </p>
            </div>
          )}

          {/* Success Message */}
          {videoBlob && !isGenerating && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-800 dark:text-green-200 font-semibold mb-2">
                ✅ Video generated successfully!
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">
                Size: {(videoBlob.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
          {!videoBlob && !isGenerating && (
            <>
              <Button
                onClick={handleGenerateVideo}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                🎬 Generate Video
              </Button>
              <Button
                onClick={onClose}
                variant="secondary"
                className="px-6"
              >
                Cancel
              </Button>
            </>
          )}

          {isGenerating && (
            <Button
              disabled
              className="flex-1 bg-gray-400 cursor-not-allowed text-white"
            >
              Generating...
            </Button>
          )}

          {videoBlob && !isGenerating && (
            <>
              <Button
                onClick={handleShare}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                📱 Share to WhatsApp
              </Button>
              <Button
                onClick={handleDownload}
                variant="secondary"
                className="px-6"
              >
                💾 Download
              </Button>
              <Button
                onClick={onClose}
                variant="secondary"
                className="px-6"
              >
                Close
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
